<script>
import { useCellSelection } from "@/composables/cellSelection.mjs";

const fmt = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export default {
  name: "CellSelectionStatusBar",
  setup() {
    const { state, stats, clearSelection } = useCellSelection();
    return { state, stats, clearSelection };
  },
  computed: {
    visible() {
      return this.state.cells.size > 0;
    },
    countText() {
      return this.stats.count;
    },
    numericText() {
      return this.stats.numericCount;
    },
    sumText() {
      return fmt(this.stats.sum);
    },
    avgText() {
      return fmt(this.stats.avg);
    },
    minText() {
      return fmt(this.stats.min);
    },
    maxText() {
      return fmt(this.stats.max);
    },
  },
  methods: {
    async copyToClipboard() {
      const tsv = this.buildTsv();
      try {
        await navigator.clipboard.writeText(tsv);
      } catch (e) {
        const ta = document.createElement("textarea");
        ta.value = tsv;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { document.execCommand("copy"); } catch (_) { /* noop */ }
        document.body.removeChild(ta);
      }
      this.toast(`Copied ${this.state.cells.size} cells`);
    },
    buildTsv() {
      const cells = Array.from(this.state.cells.values());
      if (cells.length === 0) return "";
      let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
      for (const c of cells) {
        if (c.rowIdx < minR) minR = c.rowIdx;
        if (c.rowIdx > maxR) maxR = c.rowIdx;
        if (c.colIdx < minC) minC = c.colIdx;
        if (c.colIdx > maxC) maxC = c.colIdx;
      }
      const byKey = new Map(cells.map((c) => [`${c.rowIdx}:${c.colIdx}`, c.rawText]));
      const rows = [];
      for (let r = minR; r <= maxR; r++) {
        const row = [];
        for (let c = minC; c <= maxC; c++) {
          row.push(byKey.get(`${r}:${c}`) ?? "");
        }
        rows.push(row.join("\t"));
      }
      return rows.join("\n");
    },
    toast(msg) {
      const el = document.createElement("div");
      el.textContent = msg;
      el.className = "cell-selection-toast";
      document.body.appendChild(el);
      setTimeout(() => el.classList.add("visible"), 10);
      setTimeout(() => {
        el.classList.remove("visible");
        setTimeout(() => el.remove(), 300);
      }, 1800);
    },
  },
};
</script>

<template>
  <div
    v-if="visible"
    class="cell-selection-bar"
    role="status"
    aria-live="polite"
  >
    <span><span class="stat-label">Count:</span><span class="stat-value">{{ countText }}</span></span>
    <span><span class="stat-label">Numeric:</span><span class="stat-value">{{ numericText }}</span></span>
    <span><span class="stat-label">Sum:</span><span class="stat-value">{{ sumText }}</span></span>
    <span><span class="stat-label">Avg:</span><span class="stat-value">{{ avgText }}</span></span>
    <span><span class="stat-label">Min:</span><span class="stat-value">{{ minText }}</span></span>
    <span><span class="stat-label">Max:</span><span class="stat-value">{{ maxText }}</span></span>
    <span class="btn-bar">
      <button
        type="button"
        class="btn btn-sm btn-outline-light me-2"
        data-testid="cell-selection-copy"
        @click="copyToClipboard"
      >
        <i class="ri-file-copy-line"></i> Copy
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-light"
        data-testid="cell-selection-clear"
        @click="clearSelection"
      >
        <i class="ri-close-line"></i> Clear
      </button>
    </span>
  </div>
</template>
